import { DOCUMENT } from "@angular/common";
import { inject, Injectable, Renderer2, RendererFactory2 } from "@angular/core";

interface InjectURLOptions {
	defer?: boolean;
	async?: boolean;
	location: "head" | "body";
	type?: string;
}

@Injectable()
export class ScriptInjectorService {
	private document = inject(DOCUMENT);

	private renderer2: Renderer2;

	constructor() {
		const rendererFactory = inject(RendererFactory2);

		this.renderer2 = rendererFactory.createRenderer(null, null);
	}

	/**
	 * Inject a URL into the document.
	 *
	 * @param url - URL to be injected.
	 * @param options - Options regarding the script.
	 */
	injectUrl(url: string, options: InjectURLOptions): unknown {
		const scriptTag = this.renderer2.createElement("script");

		this.renderer2.setAttribute(scriptTag, "type", "text/javascript");
		this.renderer2.setAttribute(scriptTag, "src", url);

		if (options) {
			if (options.defer) {
				this.renderer2.setAttribute(scriptTag, "defer", "");
			}

			if (options.async) {
				this.renderer2.setAttribute(scriptTag, "async", "");
			}

			if (options.type) {
				this.renderer2.setAttribute(scriptTag, "type", options.type);
			}
		}

		this.renderer2.appendChild(this.document[options.location], scriptTag);

		return scriptTag;
	}

	/**
	 * Remove child element from parent element in DOM.
	 *
	 * @param parent - Parent of the element that needs to be removed.
	 * @param element - Element that needs to be removed.
	 */
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	removeElement(parent: "body" | "head", element: any): void {
		this.renderer2.removeChild(this.document[parent], element);
	}

	/**
	 * Inject script in DOM.
	 *
	 * @param script - String with functionalities.
	 * @param options - Possible attributes to add to the script element.
	 */
	injectScript(script: string, options: Omit<InjectURLOptions, "type">): unknown {
		const scriptTag = this.renderer2.createElement("script");

		this.renderer2.setAttribute(scriptTag, "type", "text/javascript");

		if (options) {
			if (options.defer) {
				this.renderer2.setAttribute(scriptTag, "defer", "");
			}

			if (options.async) {
				this.renderer2.setAttribute(scriptTag, "async", "");
			}
		}

		this.renderer2.setProperty(scriptTag, "innerHTML", script);

		this.renderer2.appendChild(this.document[options.location], scriptTag);

		return scriptTag;
	}
}
